const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

type AuthFailureHandler = () => void;
type RefreshHandler = () => Promise<boolean>;

let onAuthFailure: AuthFailureHandler = () => {};
let refreshSession: RefreshHandler = async () => false;

export function configureApiClient(config: {
  onLogout: AuthFailureHandler;
  refreshSession: RefreshHandler;
}) {
  onAuthFailure = config.onLogout;
  refreshSession = config.refreshSession;
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth = false, skipRefresh = false, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  // On 401 for authenticated requests, try refreshing the session once and retry.
  if (response.status === 401 && !skipAuth && !skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) {
      const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...fetchOptions,
        headers,
        credentials: 'include',
      });

      if (retryResponse.status === 401) {
        onAuthFailure();
        throw new Error('Authentication failed');
      }

      const retryData = await parseJsonResponse(retryResponse);
      if (!retryResponse.ok) {
        throw new ApiError(
          retryData.error_description || retryData.error || 'Request failed',
          retryResponse.status,
          retryData.details
        );
      }
      return retryData as T;
    } else {
      onAuthFailure();
      throw new Error('Authentication failed');
    }
  }

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new ApiError(
      data.error_description || data.error || 'Request failed',
      response.status,
      data.details
    );
  }

  return data as T;
}

export class ApiError extends Error {
  status: number;
  details?: string[];

  constructor(message: string, status: number, details?: string[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

type JsonPayload = {
  error?: string;
  error_description?: string;
  details?: string[];
} & Record<string, unknown>;

// A proxy timeout, gateway error, or request-size limit can reject a request
// before it ever reaches the Rails app, coming back with an empty or non-JSON
// body. `response.json()` then throws a raw SyntaxError ("Unexpected end of
// JSON input") that no caller can render — turn it into an ApiError carrying
// the status instead.
async function parseJsonResponse(response: Response): Promise<JsonPayload> {
  try {
    return (await response.json()) as JsonPayload;
  } catch {
    if (!response.ok) {
      throw new ApiError(`Request failed (${response.status})`, response.status);
    }
    return {};
  }
}

async function uploadFile<T>(
  endpoint: string,
  file: File,
  fieldName: string = 'file',
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, skipRefresh = false, ...fetchOptions } = options;

  const formData = new FormData();
  formData.append(fieldName, file);

  const headers: HeadersInit = {
    ...fetchOptions.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (response.status === 401 && !skipAuth && !skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) {
      const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...fetchOptions,
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      if (retryResponse.status === 401) {
        onAuthFailure();
        throw new Error('Authentication failed');
      }

      const retryData = await parseJsonResponse(retryResponse);
      if (!retryResponse.ok) {
        throw new ApiError(
          retryData.error_description || retryData.error || 'Upload failed',
          retryResponse.status,
          retryData.details
        );
      }
      return retryData as T;
    } else {
      onAuthFailure();
      throw new Error('Authentication failed');
    }
  }

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new ApiError(
      data.error_description || data.error || 'Upload failed',
      response.status,
      data.details
    );
  }

  return data as T;
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  upload: <T>(endpoint: string, file: File, fieldName?: string, options?: RequestOptions) =>
    uploadFile<T>(endpoint, file, fieldName, options),
};
